import { Link, NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { useDemoRole } from '../auth/useMembership'
import { DemoNotice } from '../components/DemoNotice'
import { ResetBanner } from '../components/ResetBanner'

const NAV = [
  { to: '/projects', label: 'Projects' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/materials', label: 'Catalog' },
  { to: '/reports', label: 'Reports' },
] as const

export function AppShell() {
  const { user, signOutNow } = useAuth()
  const { data: membership } = useDemoRole()
  return (
    <div className="min-h-screen flex flex-col bg-blueprint-soft">
      <DemoNotice />
      <ResetBanner />
      <header className="border-b border-surface-700 bg-surface-950/95 backdrop-blur-sm sticky top-0 z-30">
        {/* Top row — brand + identity + sign-out */}
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-3 sm:gap-6">
          <Link to="/projects" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-8 h-8 sm:w-9 sm:h-9 grid place-items-center bg-accent-500 text-surface-950 font-display font-bold text-sm sm:text-base group-hover:rotate-3 transition leading-none">
              SI
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm sm:text-lg text-surface-50">
                Site Inspection
              </span>
              <span className="hidden sm:block text-[12px] text-surface-400">
                For builders
              </span>
            </span>
          </Link>

          <div className="flex-1" />

          {membership?.role && (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 border border-accent-500/40 bg-accent-500/10 shrink-0">
              <span className="w-1.5 h-1.5 bg-accent-500 rounded-full" />
              <span className="text-[12px] text-accent-400 capitalize">
                {membership.role}
              </span>
            </span>
          )}
          <span
            className="hidden lg:inline-block text-[12px] text-surface-400 max-w-[180px] truncate"
            title={user?.email ?? ''}
          >
            {user?.email}
          </span>
          <button
            type="button"
            onClick={() => void signOutNow()}
            className="text-[12px] sm:text-[13px] border border-surface-700 hover:border-rebar-500 hover:text-rebar-500 text-surface-100 px-2.5 sm:px-3 py-1.5 transition shrink-0"
          >
            Sign out
          </button>
        </div>

        {/* Bottom row — nav (own row so it never crowds the brand on mobile) */}
        <nav className="max-w-6xl mx-auto px-1 sm:px-4 flex gap-0 sm:gap-1 overflow-x-auto border-t border-surface-700/60">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `relative px-3 sm:px-4 py-2.5 whitespace-nowrap transition text-[14px] font-medium ${
                  isActive
                    ? 'text-accent-400 [&>.bar]:opacity-100'
                    : 'text-surface-100 hover:text-surface-50'
                }`
              }
            >
              {n.label}
              <span
                className="bar absolute -bottom-px left-2 right-2 sm:left-4 sm:right-4 h-0.5 bg-accent-500 opacity-0 transition"
                aria-hidden
              />
            </NavLink>
          ))}
        </nav>

        {/* Mobile-only role chip + email row (since we hid them above on small screens) */}
        {(membership?.role || user?.email) && (
          <div className="sm:hidden border-t border-surface-700/60 px-3 py-1.5 flex items-center gap-2 text-[11px] text-surface-400">
            {membership?.role && (
              <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 border border-accent-500/40 bg-accent-500/10">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full" />
                <span className="text-accent-400 capitalize text-[11px]">
                  {membership.role}
                </span>
              </span>
            )}
            {user?.email && (
              <span className="truncate" title={user.email}>
                {user.email}
              </span>
            )}
          </div>
        )}
      </header>
      <main className="flex-1 max-w-6xl mx-auto w-full px-3 sm:px-6 py-5 sm:py-8">
        <Outlet />
      </main>
      <footer className="border-t border-surface-700 bg-surface-950">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row gap-1.5 sm:gap-2 sm:items-center sm:justify-between text-[12px] sm:text-[13px] text-surface-400">
          <span>
            <span className="text-surface-100">Site Inspection</span> · demo · beta
          </span>
          <a
            href="mailto:site-inspection-app@prashamhtrivedi.in?subject=Onboard%20our%20crew%20to%20Site%20Inspection"
            className="hover:text-accent-400 transition break-all"
          >
            Want it for your sites? site-inspection-app@prashamhtrivedi.in
          </a>
        </div>
      </footer>
    </div>
  )
}
